// Industries data for custom course creation
export interface Industry {
  name: string;
  slug: string; // Used for fetching prompts from Supabase (like ticker for stocks)
  // Format: lowercase, hyphenated (e.g., 'ai-compute', 'semiconductors')
  // This slug is passed as 'ticker' parameter to callPerplexityAPI and generateChapter
  description: string;
}

export const INDUSTRIES: Industry[] = [
  {
    name: 'Artificial Intelligence & Compute',
    slug: 'ai-compute',
    description: 'Foundation models, AI software, inference, edge AI',
  },
  {
    name: 'Semiconductors',
    slug: 'semiconductors',
    description: 'Logic, memory, equipment, chip design',
  },
  {
    name: 'Data Centers & Digital Infrastructure',
    slug: 'data-centers-infrastructure',
    description: 'Cloud, colocation, networking, power + cooling',
  },
  {
    name: 'Energy (Traditional)',
    slug: 'energy-traditional',
    description: 'Oil, gas, LNG, refining, energy services',
  },
  {
    name: 'Energy Transition & Renewables',
    slug: 'energy-renewables',
    description: 'Solar, wind, hydrogen, carbon capture',
  },
  {
    name: 'Materials & Mining',
    slug: 'materials-mining',
    description: 'Copper, lithium, rare earths, steel, chemicals',
  },
  {
    name: 'Industrials & Advanced Manufacturing',
    slug: 'industrials-manufacturing',
    description: 'Machinery, automation, robotics, factory tech',
  },
  {
    name: 'Transportation & Logistics',
    slug: 'transportation-logistics',
    description: 'Rail, shipping, trucking, aviation, supply chains',
  },
  {
    name: 'Healthcare & Life Sciences',
    slug: 'healthcare-life-sciences',
    description: 'Pharma, biotech, devices, diagnostics',
  },
  {
    name: 'Healthcare Services & Tech',
    slug: 'healthcare-services-tech',
    description: 'Hospitals, insurers, digital health, AI diagnostics',
  },
  {
    name: 'Financial Services',
    slug: 'financial-services',
    description: 'Banks, asset managers, insurers',
  },
  {
    name: 'Fintech & Payments',
    slug: 'fintech-payments',
    description: 'Digital wallets, embedded finance, neobanks',
  },
  {
    name: 'Consumer Goods (FNB)',
    slug: 'consumer-goods-fnb',
    description: 'Food, beverages, household products',
  },
  {
    name: 'Consumer Discretionary (Fashion and Travel)',
    slug: 'consumer-discretionary',
    description: 'Apparel, autos, travel, luxury, leisure',
  },
  {
    name: 'Software & SaaS',
    slug: 'software-saas',
    description: 'Enterprise software, productivity, cybersecurity',
  },
  {
    name: 'Media, Gaming & Entertainment',
    slug: 'media-gaming-entertainment',
    description: 'Streaming, gaming, social platforms, content',
  },
  {
    name: 'Real Estate & Built Environment',
    slug: 'real-estate-built-environment',
    description: 'Residential, commercial, REITs, infrastructure',
  },
  {
    name: 'Utilities & Grid Infrastructure',
    slug: 'utilities-grid-infrastructure',
    description: 'Electric utilities, water, transmission, storage',
  },
  {
    name: 'Defense, Aerospace & Security',
    slug: 'defense-aerospace-security',
    description: 'Defense contractors, space, surveillance',
  },
  {
    name: 'Precious Metals & Real Assets',
    slug: 'precious-metals-real-assets',
    description: 'Gold, silver, royalties, inflation hedges',
  },
];

// Helper function to get industry by slug
export function getIndustryBySlug(slug: string): Industry | undefined {
  return INDUSTRIES.find((industry) => industry.slug === slug);
}

// Industry-specific chapters (different from stock-based chapters)
export interface IndustryChapter {
  value: string;
  label: string;
  description: string;
}

export const INDUSTRY_CHAPTERS: IndustryChapter[] = [
  {
    value: 'industry-foundations-value-creation',
    label: 'Industry Foundations & Value Creation',
    description: 'Understanding how the industry creates value and fundamental business models',
  },
  {
    value: 'industry-ecosystem-value-chain',
    label: 'Industry Ecosystem & Value Chain',
    description: 'Mapping the industry ecosystem, supply chains, and key relationships',
  },
  {
    value: 'industry-cycles-timing-macro',
    label: 'Industry Cycles, Timing & Macro',
    description: 'Industry cycles, timing factors, and macroeconomic influences',
  },
  {
    value: 'competitive-landscape-power-geopolitics',
    label: 'Competitive Landscape, Power & Geopolitics',
    description: 'Competitive dynamics, market power, and geopolitical considerations',
  },
  {
    value: 'investment-vehicles-exposure-design',
    label: 'Investment Vehicles & Exposure Design',
    description: 'Ways to gain exposure to the industry through various investment vehicles',
  },
  {
    value: 'company-asset-evaluation-framework',
    label: 'Company & Asset Evaluation Framework',
    description: 'Framework for evaluating companies and assets within the industry',
  },
  {
    value: 'risk-scenarios-failure-modes',
    label: 'Risk Scenarios & Failure Modes',
    description: 'Key risks, failure scenarios, and what could go wrong',
  },
  {
    value: 'long-term-outlook-portfolio-integration',
    label: 'Long-Term Outlook & Portfolio Integration',
    description: 'Long-term industry outlook and how to integrate into a portfolio',
  },
];

// Goal-based chapter ordering for industries
export const INDUSTRY_GOAL_CHAPTER_ORDER: { [key: string]: string[] } = {
  quick: [
    'Industry Foundations & Value Creation',
    'Industry Ecosystem & Value Chain',
    'Competitive Landscape, Power & Geopolitics',
  ],
  solid: [
    'Industry Foundations & Value Creation',
    'Industry Ecosystem & Value Chain',
    'Industry Cycles, Timing & Macro',
    'Competitive Landscape, Power & Geopolitics',
    'Investment Vehicles & Exposure Design',
    'Company & Asset Evaluation Framework',
  ],
  deep: [
    'Industry Foundations & Value Creation',
    'Industry Ecosystem & Value Chain',
    'Industry Cycles, Timing & Macro',
    'Competitive Landscape, Power & Geopolitics',
    'Investment Vehicles & Exposure Design',
    'Company & Asset Evaluation Framework',
    'Risk Scenarios & Failure Modes',
    'Long-Term Outlook & Portfolio Integration',
  ],
};
